/**
 * Pure builders for the customer (second) screen message sent to the Android wrapper through
 * window.AndroidPOS.updateCustomerDisplay(json). No DOM, no network, no side effects - the bridge
 * (customerDisplayBridge.js) is what actually sends. Contract version 1:
 *
 *   { v: 1, state: 'idle' | 'cart' | 'payment', currency: '$',
 *     items: [{ cart_id, name, qty, unit_price, price, image?, details: [], children?: [{ name, qty, details }] }],
 *     totals?: { subtotal, surcharges: [{name, amount}], discounts: [{label, amount}],
 *                loyalty: {points, amount} | null, vouchers: [{code, amount}], total } }   // in 'cart' and 'payment'
 *
 * Both cart-shaped rows (cart page: item_name / item_quantity / first_image_url / product_detail_text)
 * and payment-shaped rows (checkout-dish-details: product_name / quantity / product_thumbnail /
 * product_detail) are understood, so the second screen shows the same facts on either screen.
 */

export const CUSTOMER_DISPLAY_VERSION = 1;

// Cart comments are shown on the POS cart/checkout rows, so the same line is sent here. Flip to
// false if staff notes should never face the customer.
export const INCLUDE_COMMENTS = true;

export const round2 = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.round((v + Number.EPSILON) * 100) / 100;
};

const firstDefined = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== '');

const ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };

const decodeEntities = (s) =>
  s.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (m, e) => {
    if (e[0] === '#') {
      const code = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : m;
    }
    const v = ENTITIES[e.toLowerCase()];
    return v === undefined ? m : v;
  });

// "Name (qty)" / "Name (qty) : $1.50" as printed by CartHelper::printCartIngredients*(). A qty of 0
// is a removed default ingredient (shown red on the POS), so it reads as "No <name>".
const ingredientLine = (text) => {
  const m = text.match(/^(.*?)\s*\((\d+)\)\s*(?::\s*(\$[\d.,]+))?$/);
  if (!m) return text;
  const [, name, qtyStr, price] = m;
  const qty = parseInt(qtyStr, 10);
  if (!name) return text;
  const suffix = price ? `: ${price}` : '';
  if (qty === 0) return `No ${name}${suffix}`;
  if (qty === 1) return `${name}${suffix}`;
  return `${name} (${qty})${suffix}`;
};

/**
 * The POS renders product_detail_text / productDetails / dealIngrade / dealUpgrade with
 * dangerouslySetInnerHTML: a ", "-joined run of <i style='color: ..'>Name (qty)</i>, or <br/>-joined
 * lines. This flattens that to plain customer-facing lines.
 */
export const htmlToLines = (html) => {
  if (typeof html !== 'string' || !html.trim()) return [];
  const text = html
    .replace(/<\/i>/gi, '</i>\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li)>/gi, '\n')
    .replace(/<[^>]*>/g, '');
  return decodeEntities(text)
    .split('\n')
    .map((l) => l.replace(/\s+/g, ' ').replace(/^[\s,]+|[\s,]+$/g, ''))
    .filter(Boolean)
    .map(ingredientLine);
};

const commentLines = (comments) => {
  if (!INCLUDE_COMMENTS || typeof comments !== 'string') return [];
  const c = comments.trim();
  return c ? [`Note: ${c}`] : [];
};

const toppingLines = (toppings) =>
  Array.isArray(toppings)
    ? toppings
        .filter((t) => t && (t.label || t.group))
        .map((t) => {
          const price = t.price !== undefined && t.price !== null && t.price !== '' ? ` ($${t.price})` : '';
          return `${t.group ? `${t.group}: ` : ''}${t.label || ''}${price}`.trim();
        })
    : [];

const sizeLine = (row) => {
  const size = firstDefined(row.selected_size, row.product_size);
  return size ? [String(size).trim()] : [];
};

const qtyOf = (row) => {
  const q = Number(firstDefined(row.item_quantity, row.quantity, 1));
  return Number.isFinite(q) && q > 0 ? q : 1;
};

const imageOf = (row) => firstDefined(row.first_image_url, row.product_thumbnail);

// cart rows carry the text in product_detail_text (product_detail is then an object); checkout rows
// carry the text in product_detail.
const detailHtml = (row) => {
  if (typeof row.product_detail_text === 'string') return row.product_detail_text;
  if (typeof row.product_detail === 'string') return row.product_detail;
  return '';
};

const withImage = (line, row) => {
  const image = imageOf(row);
  if (image) line.image = image;
  return line;
};

const sortKey = (row) => {
  const k = Number(firstDefined(row.cart_id, row.item_id, row.cid));
  return Number.isFinite(k) ? k : null;
};

const isDeleted = (row) => !!row.item_deleted;

const buildChild = (child) => ({
  name: String(firstDefined(child.item_name, child.product_name, '') || ''),
  qty: qtyOf(child),
  details: [
    ...sizeLine(child),
    ...htmlToLines(child.dealIngrade),
    ...htmlToLines(child.dealUpgrade),
    // product_detail_text only: on deal pieces product_detail is the catalogue description, which
    // the POS cart does not show for them.
    ...htmlToLines(child.product_detail_text),
    ...toppingLines(child.toppings),
    ...commentLines(child.comments),
  ],
});

const childrenOf = (parent) => {
  const list = parent[parent.cid] ?? parent[parent.cart_id];
  return Array.isArray(list) ? list.filter((c) => c && !isDeleted(c)).map(buildChild) : [];
};

const finishLine = (row, line) => {
  const id = firstDefined(row.cart_id, row.item_id);
  return withImage({ ...(id !== undefined ? { cart_id: id } : {}), ...line }, row);
};

const priceLine = (price, qty) => ({
  qty,
  unit_price: round2(qty > 0 ? price / qty : price),
  price: round2(price),
});

/**
 * Cart/checkout rows -> contract `items`. Plain items first, then deals, then half-and-half; the
 * whole list is ordered by cart id so it reads in the order things were added.
 */
export const buildItems = (cartItems, dealsItem, halfAndHalfItems) => {
  const rows = [];

  (Array.isArray(cartItems) ? cartItems : []).forEach((item) => {
    if (!item || isDeleted(item)) return;
    const qty = qtyOf(item);
    const price = Number(item.total_price) || 0;
    const unit = item.per_item_price !== undefined && item.per_item_price !== null && item.per_item_price !== ''
      ? round2(item.per_item_price)
      : priceLine(price, qty).unit_price;
    rows.push({
      key: sortKey(item),
      line: finishLine(item, {
        name: String(firstDefined(item.item_name, item.product_name, '') || ''),
        qty,
        unit_price: unit,
        price: round2(price),
        details: [...sizeLine(item), ...htmlToLines(detailHtml(item)), ...commentLines(item.comments)],
      }),
    });
  });

  (Array.isArray(dealsItem) ? dealsItem : []).forEach((deal) => {
    if (!deal || isDeleted(deal)) return;
    const qty = qtyOf(deal);
    // total_price is only the deal's own price; totalwithdeals adds the upgraded pieces.
    const price = Number(firstDefined(deal.totalwithdeals, deal.total_price)) || 0;
    const children = childrenOf(deal);
    const line = finishLine(deal, {
      name: String(firstDefined(deal.item_name, '') || ''),
      ...priceLine(price, qty),
      details: [...htmlToLines(deal.productDetails), ...htmlToLines(deal.highlighter)],
    });
    if (children.length) line.children = children;
    rows.push({ key: sortKey(deal), line });
  });

  (Array.isArray(halfAndHalfItems) ? halfAndHalfItems : []).forEach((hnh) => {
    if (!hnh || isDeleted(hnh)) return;
    const qty = qtyOf(hnh);
    const price = Number(hnh.total_price) || 0;
    const children = childrenOf(hnh);
    const details = [];
    if (hnh.crust_label) {
      const crustPrice = hnh.crust_price !== undefined && hnh.crust_price !== null && hnh.crust_price !== ''
        ? ` ($${hnh.crust_price})`
        : '';
      details.push(`Crust: ${hnh.crust_label}${crustPrice}`);
    }
    const line = finishLine(hnh, {
      name: String(firstDefined(hnh.item_name, '') || ''),
      ...priceLine(price, qty),
      details,
    });
    if (children.length) line.children = children;
    rows.push({ key: sortKey(hnh), line });
  });

  return rows
    .map((r, i) => ({ ...r, i }))
    .sort((a, b) => (a.key !== null && b.key !== null && a.key !== b.key ? a.key - b.key : a.i - b.i))
    .map((r) => r.line);
};

const positive = (n) => {
  const v = round2(n);
  return v > 0 ? v : 0;
};

/**
 * checkout-summary-details response -> contract `totals`.
 *
 * opts:
 *   voucher        { code } when the active offer on the order was applied as a voucher code; its
 *                  amount moves from `discounts` to `vouchers`
 *   loyaltyPoints  points the cashier ticked to redeem (the server decides the dollar value)
 *   dueNow         amount due for the current split, when a split payment is in progress
 *
 * Empty groups are returned empty ([] / null), never undefined.
 */
export const buildTotals = (summary, opts = {}) => {
  if (!summary || typeof summary !== 'object') return null;
  const { voucher = null, loyaltyPoints = 0, dueNow = null } = opts;

  const surcharges = [];
  (Array.isArray(summary.global_surcharges) ? summary.global_surcharges : []).forEach((s) => {
    const amount = positive(s?.total_expenses);
    if (amount) surcharges.push({ name: String(s.charge_name || 'Surcharge'), amount });
  });
  const delivery = positive(summary.delivery_fee);
  if (delivery) surcharges.push({ name: 'Delivery fee', amount: delivery });

  const discounts = [];
  const vouchers = [];
  const offerAmount = positive(summary.offer_discount);
  if (offerAmount) {
    if (voucher && voucher.code) vouchers.push({ code: String(voucher.code), amount: offerAmount });
    else discounts.push({ label: String(summary.offer_name || 'Discount'), amount: offerAmount });
  }
  const manualAmount = positive(summary.manual_discount);
  if (manualAmount) {
    const v = summary.manual_discount_value;
    const label = summary.manual_discount_type === 'percent' && v ? `Discount (${v}%)` : v ? `Discount ($${v})` : 'Discount';
    discounts.push({ label, amount: manualAmount });
  }

  // Only what the server actually took off the total: ticking the redeem box alone changes nothing
  // until the backend applies it, and the second screen must agree with the amount due.
  const loyaltyAmount = positive(summary.loyalty_discount);
  const loyalty = loyaltyAmount
    ? { points: Math.max(0, Math.round(Number(loyaltyPoints) || 0)), amount: loyaltyAmount }
    : null;

  const due = Number(dueNow);
  const total = Number.isFinite(due) && due > 0 ? round2(due) : round2(summary.net_total);

  return {
    subtotal: round2(summary.gross_total),
    surcharges,
    discounts,
    loyalty,
    vouchers,
    total,
  };
};

/**
 * Running totals for the cart screen. The cart endpoint only knows the items' net total - surcharges,
 * discounts, loyalty and vouchers are worked out on the payment screen - so those groups are empty
 * here and subtotal = total.
 */
export const buildCartTotals = (netTotal) => {
  const total = round2(netTotal);
  return { subtotal: total, surcharges: [], discounts: [], loyalty: null, vouchers: [], total };
};

/**
 * state: 'idle' | 'cart' | 'payment'. idle carries no items and no totals; 'cart' and 'payment'
 * both carry totals (cart: running total, payment: full breakdown).
 */
export const makePayload = (state, { items = [], totals = null, currency = '$' } = {}) => {
  const payload = {
    v: CUSTOMER_DISPLAY_VERSION,
    state,
    currency,
    items: state === 'idle' ? [] : Array.isArray(items) ? items : [],
  };
  if (state !== 'idle' && totals) payload.totals = totals;
  return payload;
};

// ---------------------------------------------------------------------------------------------
// Idle logo / customer-screen settings. The settings are per RESTAURANT and live on the server
// (GET /v3/configs/get-customer-screen); the web app only edits them in Settings and relays the
// effective answer to the wrapper with setCustomerDisplayOptions. Pure helpers only: the network
// calls are in customerDisplayOptions.js, the bridge call in customerDisplayBridge.js.
// ---------------------------------------------------------------------------------------------

export const MAX_LOGO_SIDE = 800;
export const MAX_LOGO_BYTES = 1024 * 1024;
export const LOGO_MIME_TYPES = ['image/png', 'image/jpeg'];

/** Fit inside max x max, keeping the aspect ratio, never upscaling. */
export const fitWithin = (width, height, max = MAX_LOGO_SIDE) => {
  const w = Number(width);
  const h = Number(height);
  if (!(w > 0) || !(h > 0)) return { width: 0, height: 0 };
  const scale = Math.min(1, max / Math.max(w, h));
  return { width: Math.max(1, Math.round(w * scale)), height: Math.max(1, Math.round(h * scale)) };
};

export class LogoError extends Error {}

/**
 * Validates and downsizes a file picked for the customer-screen logo. `downscale(file)` is injected
 * (the browser canvas implementation lives in customerDisplayOptions.js) and must resolve to a
 * PNG/JPEG Blob of at most MAX_LOGO_SIDE on the long side. Rejects with a LogoError whose message
 * is fit to show to staff.
 */
export const prepareLogoFile = async (file, downscale) => {
  if (!file || !LOGO_MIME_TYPES.includes(file.type)) {
    throw new LogoError('Please choose a PNG or JPEG image.');
  }
  let blob;
  try {
    blob = await downscale(file);
  } catch (e) {
    throw new LogoError('That image could not be read. Please choose a different file.');
  }
  if (!blob || !LOGO_MIME_TYPES.includes(blob.type) || !(blob.size > 0)) {
    throw new LogoError('That image could not be read. Please choose a different file.');
  }
  if (blob.size > MAX_LOGO_BYTES) {
    throw new LogoError('That image is still larger than 1 MB after resizing. Please choose a simpler or smaller picture.');
  }
  return blob;
};

/**
 * get-customer-screen `data` -> setCustomerDisplayOptions: { logo: { source: 'url', url } } for the
 * effective logo the server chose (custom upload > Business-details logo), or { source: 'default' }
 * (bundled icon) when there is none, the server is older, or nothing is cached yet. The idle screen
 * always shows the logo, so there is no on/off flag.
 */
export const buildOptions = (screen) => {
  const s = screen && typeof screen === 'object' ? screen : {};
  const url = typeof s.display_logo_url === 'string' ? s.display_logo_url.trim() : '';
  const options = { logo: url ? { source: 'url', url } : { source: 'default' } };
  // The "join our rewards" QR beside the logo (server: join_qr {enabled, url, image: base64 PNG, caption}). Switched on with a picture: sent.
  // Switched off, or on without a picture: the wrapper is told to show none. No join_qr at all (an older server): nothing is said.
  const q = s.join_qr && typeof s.join_qr === 'object' ? s.join_qr : null;
  if (q) {
    const image = typeof q.image === 'string' ? q.image.trim() : '';
    options.joinQr =
      q.enabled === true && image
        ? { show: true, url: typeof q.url === 'string' ? q.url.trim() : '', image, caption: typeof q.caption === 'string' && q.caption.trim() ? q.caption.trim() : 'Scan to join rewards' }
        : null;
  }
  return options;
};
